"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const FacadeEvents_1 = require("./EventDefinition/FacadeEvents");
const StepEvents_1 = require("./EventDefinition/StepEvents");
const WidgetEvents_1 = require("./EventDefinition/WidgetEvents");
const Mixin_1 = require("../Util/Mixin");
const IContainerInjection_1 = require("../Container/IContainerInjection");
/**
 * Widget facade.
 *
 * Serves as the client widget interface.
 *
 * @extendable
 */
class WidgetFacade {
    /**
     * Widget facade for client-widget communication interface.
     *
     * It uses encapsulation construction for privatization. See in:
     * @see WidgetFacadeFactory
     *
     * @param {WidgetState} widgetState
     * @param {WidgetConfig} widgetConfig
     * @param {EventContainerContext} eventContext
     * @param {Updater} widgetUpdater
     */
    constructor(widgetState, widgetConfig, eventContext, widgetUpdater) {
        this.widgetState = widgetState;
        this.widgetConfig = widgetConfig;
        this.eventContext = eventContext;
        this.widgetUpdater = widgetUpdater;
    }
    /**
     * @inheritDoc
     * @see IContainerInjection
     */
    static create(widgetState, widgetConfig, eventContext, container) {
        return new this(widgetState, widgetConfig, eventContext, container.get("widget_updater"));
    }
    /**
     * Gets methods that are private of the facade.
     *
     * These methods won't be accessible to the client application.
     *
     * @return {string[]}
     */
    static getPrivateMethods() {
        return [
            "hasOwnProperty",
            "isPrototypeOf",
            "propertyIsEnumerable",
            "toSource",
            "valueOf",
            "toString",
            "toLocaleString",
            "spewatch",
            "speunwatch"
        ];
    }
    /**
     * Gets the restricted events.
     *
     * The client application won't be able to subscribe to these.
     *
     * @return {string[]}
     */
    static getRestrictedEvents() {
        return [
            StepEvents_1.StepEvents.STEP_FINISHED,
            StepEvents_1.StepEvents.STEP_UNFINISHED,
            WidgetEvents_1.WidgetEvents.INTERNAL_ERROR
        ];
    }
    /**
     * Triggers rendering of the widget.
     *
     * @return {this}
     */
    render() {
        this.eventContext.emit(FacadeEvents_1.FacadeEvents.RENDER);
        return this;
    }
    /**
     * Triggers destruction of the widget.
     *
     * @return {this}
     */
    destroy() {
        this.eventContext.emit(FacadeEvents_1.FacadeEvents.DESTROY);
        this.eventContext.destroy();
        return this;
    }
    /**
     * Registers an event listener.
     *
     * @param {string} event
     * @param {Function} callback
     * @return {this}
     */
    on(event, callback) {
        // Only allow registering to non internal events.
        if (!this.constructor.getRestrictedEvents().includes(event)) {
            this.eventContext.on(event, callback);
        }
        return this;
    }
    /**
     * Registers event listener for all events.
     *
     * @param {Function} callback
     * @return {this}
     */
    onEvery(callback) {
        this.eventContext.onEvery((event, ...args) => {
            if (!this.constructor.getRestrictedEvents().includes(event)) {
                callback(event, ...args);
            }
        });
        return this;
    }
    /**
     * Update widget
     *
     * @param {string} type
     * @param value
     * @returns {Promise<*>}
     */
    update(type, value) {
        return this.widgetUpdater.update(type, value, this.widgetConfig, this.widgetState, this.eventContext).then((updated) => {
            this.eventContext.emit(WidgetEvents_1.WidgetEvents.UPDATED, type, value);
            this.eventContext.emit(WidgetEvents_1.WidgetEvents.UPDATED + `:${type}`);
            return updated;
        });
    }
}
exports.default = WidgetFacade;
Mixin_1.default(WidgetFacade, IContainerInjection_1.default);
